import { type User, UserManager, WebStorageStateStore } from 'oidc-client-ts';

/**
 * OIDC-клиент Интранета. Конфигурация повторяет lk-web / quotas-web —
 * единый стиль логина для всех приложений платформы.
 */
const userManager = new UserManager({
  authority: `${import.meta.env.VITE_KEYCLOAK_URL}/realms/${import.meta.env.VITE_KEYCLOAK_REALM}`,
  client_id: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  // offline_access — долгая сессия («как у приложения»): токен продления
  // живёт неделями без активности и не зависит от короткой SSO-сессии
  // браузера. Без него приложение на экране «Домой» iPhone просило пароль
  // после каждой паузы. Скоуп разрешён клиенту intranet-web в Keycloak, роль
  // offline_access у сотрудников — в ролях по умолчанию.
  scope: 'openid profile email offline_access',
  loadUserInfo: true,
  automaticSilentRenew: true,
  silent_redirect_uri: `${window.location.origin}/auth/silent-renew.html`,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
});

export { userManager };
export type { User };

export function startLogin(returnTo?: string): Promise<void> {
  return userManager.signinRedirect({
    state: returnTo ? { returnTo } : undefined,
  });
}

export async function completeLogin(): Promise<{ user: User; returnTo: string }> {
  const user = await userManager.signinRedirectCallback();
  const state = user.state as { returnTo?: string } | undefined;
  return { user, returnTo: state?.returnTo ?? '/' };
}

/**
 * Выход. Долгую (offline) сессию сначала отзываем: выход из Keycloak её не
 * закрывает, и без отзыва токен продления на устройстве жил бы дальше.
 */
export async function startLogout(): Promise<void> {
  await userManager.revokeTokens(['refresh_token']).catch(() => undefined);
  return userManager.signoutRedirect();
}

/** Продлить сессию токеном продления (без перехода на страницу входа). null — не вышло. */
export async function renewSession(): Promise<User | null> {
  const user = await userManager.getUser();
  if (!user?.refresh_token) return null;
  try {
    return await userManager.signinSilent();
  } catch {
    return null;
  }
}

export function getStoredUser(): Promise<User | null> {
  return userManager.getUser();
}
